import React, { useState, useEffect } from 'react';
import {
  Box,
  Heading,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  FormHelperText,
  Button,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  Select,
  Switch,
  useDisclosure,
  Spinner,
  Icon,
  Alert,
  AlertIcon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tooltip,
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiLayout } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const API_BASE = '/api/v1/admin-ui';

const LOCATION_LABELS = {
  banner: 'Banner',
  footer: 'Footer',
  checkout: 'Checkout',
};

const WIDGET_LABELS = {
  freeform: 'Freeform HTML',
  event_listing: 'Event Listing',
};

const LOCATION_COLORS = {
  banner: 'blue',
  footer: 'purple',
  checkout: 'green',
};

const WIDGET_COLORS = {
  freeform: 'orange',
  event_listing: 'teal',
};

const EMPTY_FORM = {
  name: '',
  location: 'banner',
  widget_type: 'freeform',
  freeform_content: '',
  priority: 0,
  start_time: '',
  end_time: '',
  active: true,
};

function PromoSlots() {
  const toast = useToast();

  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSlot, setEditingSlot] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [slotToDelete, setSlotToDelete] = useState(null);
  const [saving, setSaving] = useState(false);

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Token ${localStorage.getItem('token')}`,
    'X-Tenant-ID': localStorage.getItem('instance_id'),
  });

  useEffect(() => {
    fetchSlots();
  }, []);

  const fetchSlots = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/promo-slots/`, { headers: getAuthHeaders() });
      const data = await res.json();
      if (data.success) {
        setSlots(data.promo_slots || []);
      } else {
        toast({ title: data.message || 'Failed to load promo slots', status: 'error', duration: 3000 });
      }
    } catch {
      toast({ title: 'Error loading promo slots', status: 'error', duration: 3000 });
    }
    setLoading(false);
  };

  const openAdd = () => {
    setEditingSlot(null);
    setForm(EMPTY_FORM);
    onFormOpen();
  };

  const openEdit = (slot) => {
    setEditingSlot(slot);
    setForm({
      name: slot.name || '',
      location: slot.location || 'banner',
      widget_type: slot.widget_type || 'freeform',
      freeform_content: slot.freeform_content || '',
      priority: slot.priority ?? 0,
      start_time: slot.start_time ? slot.start_time.slice(0, 16) : '',
      end_time: slot.end_time ? slot.end_time.slice(0, 16) : '',
      active: slot.active ?? true,
    });
    onFormOpen();
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Name is required', status: 'error', duration: 3000 });
      return;
    }

    setSaving(true);
    const method = editingSlot ? 'PUT' : 'POST';
    const url = editingSlot
      ? `${API_BASE}/promo-slots/${editingSlot.id}/`
      : `${API_BASE}/promo-slots/`;

    const payload = {
      ...form,
      priority: Number(form.priority),
      start_time: form.start_time || null,
      end_time: form.end_time || null,
    };

    try {
      const res = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: editingSlot ? 'Promo slot updated' : 'Promo slot created', status: 'success', duration: 3000 });
        onFormClose();
        fetchSlots();
      } else {
        toast({ title: data.message || 'Error saving promo slot', status: 'error', duration: 3000 });
      }
    } catch {
      toast({ title: 'Error saving promo slot', status: 'error', duration: 3000 });
    }
    setSaving(false);
  };

  const confirmDelete = (slot) => {
    setSlotToDelete(slot);
    onDeleteOpen();
  };

  const handleDelete = async () => {
    if (!slotToDelete) return;
    try {
      const res = await fetch(`${API_BASE}/promo-slots/${slotToDelete.id}/`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        toast({ title: 'Promo slot deleted', status: 'success', duration: 3000 });
        fetchSlots();
      } else {
        toast({ title: data.message || 'Error deleting promo slot', status: 'error', duration: 3000 });
      }
    } catch {
      toast({ title: 'Error deleting promo slot', status: 'error', duration: 3000 });
    }
    onDeleteClose();
    setSlotToDelete(null);
  };

  if (loading) {
    return (
      <Box textAlign="center" py={20}>
        <Spinner size="xl" color="orange.500" />
        <Text mt={4}>Loading promo slots...</Text>
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={6}>
        <Heading size="lg">Promo Slots</Heading>
        <Button leftIcon={<FiPlus />} colorScheme="orange" onClick={openAdd}>
          Add Promo Slot
        </Button>
      </HStack>

      <Text color="gray.600" mb={6}>
        Promo slots display widgets (freeform HTML or event listings) at specific locations on the portal.
        Only the highest-priority active slot per location is shown at any time.
      </Text>

      <Box bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
        <Table>
          <Thead bg="gray.50">
            <Tr>
              <Th>Name</Th>
              <Th>Location</Th>
              <Th>Widget</Th>
              <Th>Priority</Th>
              <Th>Schedule</Th>
              <Th>Status</Th>
              <Th width="100px">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {slots.map(slot => (
              <Tr key={slot.id}>
                <Td fontWeight="medium">{slot.name}</Td>
                <Td>
                  <Badge colorScheme={LOCATION_COLORS[slot.location] || 'gray'}>
                    {LOCATION_LABELS[slot.location] || slot.location}
                  </Badge>
                </Td>
                <Td>
                  <Badge colorScheme={WIDGET_COLORS[slot.widget_type] || 'gray'} variant="subtle">
                    {WIDGET_LABELS[slot.widget_type] || slot.widget_type}
                  </Badge>
                </Td>
                <Td isNumeric>{slot.priority}</Td>
                <Td>
                  <Text fontSize="sm" color="gray.600">
                    {slot.start_time
                      ? new Date(slot.start_time).toLocaleString()
                      : 'Always'}
                    {slot.end_time && ` → ${new Date(slot.end_time).toLocaleString()}`}
                  </Text>
                </Td>
                <Td>
                  <Badge colorScheme={slot.active ? 'green' : 'gray'}>
                    {slot.active ? 'Active' : 'Inactive'}
                  </Badge>
                </Td>
                <Td>
                  <HStack spacing={2}>
                    <Tooltip label="Edit">
                      <IconButton
                        icon={<FiEdit2 />}
                        size="sm"
                        variant="ghost"
                        colorScheme="blue"
                        onClick={() => openEdit(slot)}
                        aria-label="Edit promo slot"
                      />
                    </Tooltip>
                    <Tooltip label="Delete">
                      <IconButton
                        icon={<FiTrash2 />}
                        size="sm"
                        variant="ghost"
                        colorScheme="red"
                        onClick={() => confirmDelete(slot)}
                        aria-label="Delete promo slot"
                      />
                    </Tooltip>
                  </HStack>
                </Td>
              </Tr>
            ))}
            {slots.length === 0 && (
              <Tr>
                <Td colSpan={7} textAlign="center" py={8}>
                  <VStack spacing={2}>
                    <Icon as={FiLayout} boxSize={8} color="gray.300" />
                    <Text color="gray.500">No promo slots yet</Text>
                    <Button size="sm" colorScheme="orange" onClick={openAdd}>
                      Create your first promo slot
                    </Button>
                  </VStack>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Add / Edit Modal */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingSlot ? 'Edit Promo Slot' : 'Add Promo Slot'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="Internal label, e.g. Summer Banner"
                  focusBorderColor="orange.500"
                />
                <FormHelperText>Used internally to identify this slot.</FormHelperText>
              </FormControl>

              <HStack width="full" spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Location</FormLabel>
                  <Select
                    value={form.location}
                    onChange={e => setForm({ ...form, location: e.target.value })}
                    focusBorderColor="orange.500"
                  >
                    <option value="banner">Banner – top of every page</option>
                    <option value="footer">Footer – bottom of every page</option>
                    <option value="checkout">Checkout – below item listing</option>
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Widget</FormLabel>
                  <Select
                    value={form.widget_type}
                    onChange={e => setForm({ ...form, widget_type: e.target.value })}
                    focusBorderColor="orange.500"
                  >
                    <option value="freeform">Freeform HTML</option>
                    <option value="event_listing">Event Listing (3 events)</option>
                  </Select>
                </FormControl>
              </HStack>

              {form.widget_type === 'freeform' && (
                <FormControl>
                  <FormLabel>HTML Content</FormLabel>
                  <ReactQuill
                    theme="snow"
                    value={form.freeform_content}
                    onChange={val => setForm({ ...form, freeform_content: val })}
                    placeholder="Your promotional HTML here..."
                    style={{ height: '200px', marginBottom: '42px' }}
                  />
                  <FormHelperText>Formatted content rendered inside the widget.</FormHelperText>
                </FormControl>
              )}

              {form.widget_type === 'event_listing' && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  The Event Listing widget automatically shows the next 3 upcoming events for your instance.
                </Alert>
              )}

              <FormControl isRequired>
                <FormLabel>Priority</FormLabel>
                <NumberInput
                  value={form.priority}
                  onChange={val => setForm({ ...form, priority: val })}
                  min={0}
                  focusBorderColor="orange.500"
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <FormHelperText>Higher number wins when multiple slots share the same location.</FormHelperText>
              </FormControl>

              <HStack width="full" spacing={4}>
                <FormControl>
                  <FormLabel>Start Time</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.start_time}
                    onChange={e => setForm({ ...form, start_time: e.target.value })}
                    focusBorderColor="orange.500"
                  />
                  <FormHelperText>Leave blank to start immediately.</FormHelperText>
                </FormControl>

                <FormControl>
                  <FormLabel>End Time</FormLabel>
                  <Input
                    type="datetime-local"
                    value={form.end_time}
                    onChange={e => setForm({ ...form, end_time: e.target.value })}
                    focusBorderColor="orange.500"
                  />
                  <FormHelperText>Leave blank for no expiry.</FormHelperText>
                </FormControl>
              </HStack>

              <FormControl display="flex" alignItems="center">
                <FormLabel mb={0}>Active</FormLabel>
                <Switch
                  isChecked={form.active}
                  onChange={e => setForm({ ...form, active: e.target.checked })}
                  colorScheme="orange"
                  ml={3}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>Cancel</Button>
            <Button colorScheme="orange" onClick={handleSave} isLoading={saving}>
              {editingSlot ? 'Update' : 'Create'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Promo Slot</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Are you sure you want to delete <strong>{slotToDelete?.name}</strong>? This cannot be undone.
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>Cancel</Button>
            <Button colorScheme="red" onClick={handleDelete}>Delete</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default PromoSlots;
